import axios, { AxiosRequestConfig, AxiosResponse, AxiosError } from 'axios';
import { useCallback, useState } from 'react';
import type { InternalAxiosRequestConfig } from 'axios';
import { getApiBaseUrl } from './httpConfig';
export { getApiBaseUrl };

const api = axios.create({
  baseURL: getApiBaseUrl(),
  timeout: 10000,
});

// Request interceptor
api.interceptors.request.use(
  (config: InternalAxiosRequestConfig) => config,
  (error: AxiosError) => Promise.reject(error)
);

// Response interceptor
api.interceptors.response.use(
  (response: AxiosResponse) => response,
  (error: AxiosError) => {
    // Handle global errors here
    if (error.response) {
      const status = error.response.status;
      if (status === 404) {
        window.location.href = '/404';
      } else if (status === 500) {
        window.location.href = '/500';
      } else if (status === 401) {
        // Optionally handle unauthorized
      }
      // Note: 400-level errors (validation) are NOT redirected — they are
      // propagated to the caller so inline error messages can be shown.
    }
    return Promise.reject(error);
  }
);

// Real GET hook with state
export function useApiGet<T = any>(url: string, config?: InternalAxiosRequestConfig) {
  const [data, setData] = useState<T | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<AxiosError | null>(null);

  /**
   * Fetch data from API
   * @param options Optional callbacks: onSuccess, onError
   */
  const fetch = useCallback(
    async (options?: { onSuccess?: (data: T) => void; onError?: (err: AxiosError) => void }) => {
      setLoading(true);
      setError(null);
      try {
        const response = await api.get<T>(url, config);
        setData(response.data);
        if (options?.onSuccess) options.onSuccess(response.data);
        return response.data;
      } catch (err) {
        setError(err as AxiosError);
        if (options?.onError) options.onError(err as AxiosError);
        return undefined;
      } finally {
        setLoading(false);
      }
    },
    [url, config]
  );

  return { data, loading, error, fetch };
}

// POST hook with state and trigger
export function useApiPost<T = any>(url: string) {
  const [data, setData] = useState<T | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<AxiosError | null>(null);

  /**
   * Post data to API
   * @param postData Data to post
   * @param config Axios config
   * @param options Optional callbacks: onSuccess, onError
   */
  const post = useCallback(
    async (
      postData?: any,
      config?: InternalAxiosRequestConfig,
      options?: { onSuccess?: (data: T) => void; onError?: (err: AxiosError) => void }
    ) => {
      setLoading(true);
      setError(null);
      try {
        const response = await api.post<T>(url, postData, config);
        setData(response.data);
        if (options?.onSuccess) options.onSuccess(response.data);
        return response.data;
      } catch (err) {
        setError(err as AxiosError);
        if (options?.onError) options.onError(err as AxiosError);
        // Do not throw, just return undefined
        return undefined;
      } finally {
        setLoading(false);
      }
    },
    [url]
  );

  return { data, loading, error, post };
}

export default api;
